import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import {
  selectHasBundled,
  selectHeaderStateServiceError,
  selectInfo,
  selectIsBtMasterOnly,
  selectIsEmployee,
  selectIsPtrs,
  selectRegType,
  selectLoadTransactionsCallFinished,
  selectMoveMoneyDataAccess,
  selectSubAccountId,
  selectTransactionAuthorizationDetails,
  selectUseTestTrustDigitalToPdfFlow,
  selectHasGoingDigitalFlowWireCreateSloa,
  selectIsEstateOrTestTrust,
  selectHasGoingDigitalFlowInternalTransferCreateSloa,
  selectIsValidRegType,
  selectAccountInfoSuccessfullyCompleted,
} from '../../../account-info-header/reducers';
import { RadioControl } from '../../../create-request/models/form/FormControlRadio';
import {
  TaxWithholdingFields,
} from '../../../create-request/models/form/TransactionForm';
import { CashieringRequestApr } from '../../../create-request/models/internal-transfer/CashieringRequestApr';
import { RecurringRequest } from '../../../create-request/models/internal-transfer/RecurringRequest';
import { SloaRequest } from '../../../create-request/models/internal-transfer/SloaRequest';
import { createControl, updateGroupValueAndValidity } from '../../../create-request/utilities/formUtilities';
import {
  selectCancelModalContinueClicked,
  selectPageScrollTo,
} from '../../../shared/reducers';
import { displayCancelModal, resetCancelModal } from '../../../shared/actions/shared.actions';
import { selectStandingAuthTableActionLinkState } from '../../../shared/reducers';
import { ActionCenterRedirectService } from '../../../shared/services/ActionCenterRedirect.service';
import { TableComponentState, selectedRowData } from '../../../shared/types/TableComponentState';
import { TransactionType } from '../../../shared/types/Transaction';
import {
  ApiEndpointType,
  Constants,
  navRouteConstants,
  shortRegTypes,
  sloaAuthorization,
  transactionTypeLabels,
  transactionTypes,
  RegTypeCode,
} from '../../../shared/utilities/Constants';
import {
  StandingAuthAchActions,
  StandingAuthCheckActions,
  StandingAuthFormActions,
  StandingAuthInternalTransferActions,
  StandingAuthReviewPageActions,
  StandingAuthWireActions,
} from '../../actions';
import {
  StandingAuthDetails,
  AchExtraPrompts, ExtraPrompts, StandingAuthFormServiceError, StandingAuthorizationForm,
  CheckExtraPrompts,
} from '../../models/standingAuthorizationForm';
import {
  selectAchInquiryCallFinished,
  selectCheckInputCallFinished,
  selectCheckInputCallSucceeded,
  selectCheckVerifyCallError,
  selectInternalTransferInputCallFinished,
  selectInternalTransferVerifyCallError,
  selectIsFromEditInProgress,
  selectStagedStandingAuthForm,
  selectStandingAuthCallSucceededAch,
  selectStandingAuthForm,
  selectStandingAuthFormPrefilledState,
  selectStandingAuthFormServiceError,
  selectStandingAuthACHTaxWithholdings,
  selectTwePopupInfo,
  selectTweRecurringTransactions,
  selectTweStandingAuth,
  selectTweTableState,
  selectTweUpcomingTransactions,
  selectSLOAVerifyCallFinished,
  selectWireInputV3CallFinished,
  selectCheckInquiryCallFinished,
  selectShouldDisableFieldForEditQcd,
  selectIsFromEditStarted,
  selectStandingAuthInternalTransferInfo,
} from '../../reducers';
import { AccountInfoState } from '../../../account-info-header/models/AccountInfoState';
import { initialStandingAuthFormServiceError } from '../../reducers/standing-authorizations-form.reducer';
import { StandingAuthorizationsRequest } from '../../../create-request/models/Standing-Authorizations';
import { StandingAuthorizationsActions } from '../../../create-request/actions';
import { ModifiedFirstPartiesActions } from '../../../modified-first-parties/actions';
import { selectHasAccessInitiateMoneyMovement } from '../../../authorize/reducers';
import { CoreService } from '@charlesschwab/sac-ui-core';
import { ValidateWireFlagsService } from '../../../shared/services/validate-WireFlags.service';
import { isPalAccountFlow } from '../../../shared/utilities/AccountFlow';

@Component({
  standalone: false,
  selector: 'mm-standing-authorizations-form',
  templateUrl: './standing-authorizations-form.component.html',
  styleUrls: ['./standing-authorizations-form.component.scss'],
})
export class StandingAuthorizationsFormComponent implements OnInit, OnDestroy {
  @Output() showTweModal = new EventEmitter<boolean>();
  @Input() isEditSubmittedProfile$: Observable<boolean>;
  prefilledPromptsObs = new BehaviorSubject<StandingAuthDetails | null>(null);
  standingAuthorizationsForm: FormGroup;
  hasMoveMoneyDataAccess: boolean | undefined;
  isEmployee: boolean;
  initialTransactionTypeControl: RadioControl = {
    title: 'Transaction type',
    controlName: 'transactionType',
    radios: [],
    validationMessage: 'Select a transaction type',
  };
  transactionType: TransactionType;
  memoizedForm: StandingAuthorizationForm;
  destroy$ = new Subject<void>();
  sloaAuthorizations: string[];
  hasHeaderStateError: boolean;
  formSpinnerState$: Observable<{ loading: boolean }>;
  readyForWireStandingAuthDetails$ = new BehaviorSubject<boolean>(false);
  readyForCheckStandingAuthDetails$ = new BehaviorSubject<boolean>(false);
  isPtrs: boolean;
  regType: string;
  isValidRegType: boolean | undefined;
  standingAuthForm: StandingAuthorizationForm;
  itInputCallFinished: boolean;
  checkInputCallFinished: boolean;
  hasATSSystemError: boolean;
  atsSystemErrorText: string;
  wireInputCallFinished: boolean;
  wireInputV3CallFinished: boolean;
  tweUpcomingTransactions$: Observable<CashieringRequestApr[]>;
  tweStandingAuth$: Observable<SloaRequest[]>;
  tweRecurringTransactions$: Observable<RecurringRequest[]>;
  taxWithholdings$: Observable<TaxWithholdingFields>;
  tweTableState$: Observable<TableComponentState>;
  createRequestAccessDeniedText = Constants.createRequestAccessDeniedText;
  transactionUnauthorizedText = Constants.transactionTypeUnauthorized;
  digitalToPdfIneligibleText = Constants.digitalToPdfIneligibleEAuthorizationCopy;
  notSupportedErrorText: string = Constants.toAccountValidationError;
  palAccountNotSupportedErrorText: string = Constants.palAccountValidationError;
  stagedStandingAuthForm: StandingAuthorizationForm;
  prefilledForm: StandingAuthorizationForm | undefined;
  standingAuthFormSubmitted: StandingAuthorizationForm;
  selectedRowDataInContext: selectedRowData;
  standingAuthFormHeader: string = 'Create standing authorization';
  isEditSubmittedProfile: boolean = false;
  prefilledPromptsObsExtra = new BehaviorSubject<ExtraPrompts | null>(null);
  accountInfoState: AccountInfoState;
  readyForTransactionDetails$: Subject<boolean> = new Subject();
  isEditReady: boolean = false;
  serviceError: StandingAuthFormServiceError;
  standingAuthFormServiceError$: Observable<StandingAuthFormServiceError>;
  isFromEdit: boolean;
  useTestTrustDigitalToPdfFlow: boolean = false;
  profilesUpdated: boolean;
  registrationType: string;
  registrationTypeToAcc: string;
  loadTransactionsSuccess: boolean;
  subAccountId: string;
  toggleOff: boolean = true;
  hasBundledOffer: boolean = false;
  isBtMasterOnly: boolean = false;
  shouldDisableForEditQcd: boolean;
  achInquiryCallSuccessful: boolean;
  checkInquiryCallSuccessful: boolean;
  isInitRelease: boolean;
  hasGoingDigitalFlowWireCreateSloa: boolean;
  isEstateOrTestTrust: boolean;
  hasGoingDigitalFlowCheckCreateSloa: boolean;
  hasGoingDigitalFlowInternalTransferCreateSloa: boolean;
  skipJournalsVerify: boolean;
  isPalAccount: boolean;

  constructor(
    private fb: FormBuilder,
    private nnfb: NonNullableFormBuilder,
    private store: Store,
    private actionCenterRedirectService: ActionCenterRedirectService,
    public coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }

  ngOnInit(): void {
    this.setPalAccountFlow();
    this.standingAuthorizationsForm = this.fb.group({
      transactionType: this.nnfb.control('', Validators.required),
    });
    this.store.select(selectHasBundled).pipe(takeUntil(this.destroy$)).subscribe(hasBundled => this.hasBundledOffer = hasBundled);

    this.formSpinnerState$ = this.store.select(selectStandingAuthForm).pipe(
      takeUntil(this.destroy$),
      map((state) => {
        return { loading: state.loading };
      })
    );

    this.standingAuthFormServiceError$ = this.store.select(
      selectStandingAuthFormServiceError
    );
    this.standingAuthFormServiceError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((serviceError) => {
        this.serviceError = serviceError;
      });

    this.standingAuthorizationsForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const allValues = this.standingAuthorizationsForm.getRawValue();
        const isMemoized =
          JSON.stringify(allValues) === JSON.stringify(this.memoizedForm);
        if (!isMemoized) {
          this.memoizedForm = allValues;
          this.store.dispatch(
            StandingAuthFormActions.updateFormValue(allValues)
          );
        }
      });

    this.standingAuthorizationsForm.statusChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((status: string) => {
        let valid: boolean = status === 'VALID';
        this.store.dispatch(
          StandingAuthFormActions.updateFormStatus({ valid })
        );
      });

    this.store
      .select(selectHasAccessInitiateMoneyMovement)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasAccess) => {
        this.isInitRelease = hasAccess;
      });


    this.store
      .select(selectInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.accountInfoState = info;
        this.isPtrs = info.classifications.isPtrs;
        this.regType = info.registrationType;
      });

    this.store
      .select(selectStagedStandingAuthForm)
      .pipe(takeUntil(this.destroy$))
      .subscribe((form) => {
        this.stagedStandingAuthForm = form;
      });

    this.store
      .select(selectStandingAuthTableActionLinkState)
      .pipe(
        takeUntil(this.destroy$),
        filter((actionLink) => actionLink.actionClicked)
      )
      .subscribe((actionLinkState) => {
        this.scrollToTop();
        this.selectedRowDataInContext = actionLinkState.selectedRowData;
      });

    this.standingAuthFormServiceError$ = this.store.select(
      selectStandingAuthFormServiceError
    );
    this.standingAuthFormServiceError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((serviceError) => {
        this.serviceError = serviceError;
      });

    this.store
      .select(selectHeaderStateServiceError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasError) => {
        this.hasHeaderStateError = hasError;
      });

    combineLatest([
        this.store.select(selectWireInputV3CallFinished)
      ]).pipe(takeUntil(this.destroy$)).subscribe(([wireInputV3CallFinished]) => {
        this.readyForWireStandingAuthDetails$.next(wireInputV3CallFinished);
    })

    this.store
      .select(selectCheckInputCallSucceeded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((checkInputCallSucceeded) => {
        this.readyForCheckStandingAuthDetails$.next(checkInputCallSucceeded);
      });

    this.store
      .select(selectMoveMoneyDataAccess)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasMoveMoneyDataAccess) => {
        this.hasMoveMoneyDataAccess = hasMoveMoneyDataAccess;
      });

    combineLatest([
      this.store.select(selectLoadTransactionsCallFinished),
      this.store.select(selectAccountInfoSuccessfullyCompleted),
      this.store.select(selectCancelModalContinueClicked),
      this.store.select(selectUseTestTrustDigitalToPdfFlow),
      this.store.select(selectHasGoingDigitalFlowWireCreateSloa),
      this.store.select(selectHasGoingDigitalFlowInternalTransferCreateSloa),
      this.store.select(selectTransactionAuthorizationDetails),
      this.store.select(selectIsEstateOrTestTrust)
    ])
    .pipe(
      filter(([
        overviewCallSuccessful, accountInfoCallSuccessful, _continueClicked, _useTestTrust,
        _hasGoingDigitalFlowWireCreateSloa, _hasGoingDigitalFlowInternalTransferCreateSloa, _details, _isEstateOrTestTrust
      ]) => overviewCallSuccessful === true && accountInfoCallSuccessful === true),
      takeUntil(this.destroy$)
    )
    .subscribe(([
      _overviewCallSuccessful, _accountInfoCallSuccessful, continueClicked, useTestTrust,
      hasGoingDigitalFlowWireCreateSloa, hasGoingDigitalFlowInternalTransferCreateSloa, details, isEstateOrTestTrust
    ]) => {
      this.useTestTrustDigitalToPdfFlow = useTestTrust;
      this.hasGoingDigitalFlowWireCreateSloa = hasGoingDigitalFlowWireCreateSloa;
      this.hasGoingDigitalFlowInternalTransferCreateSloa = hasGoingDigitalFlowInternalTransferCreateSloa;
      this.sloaAuthorizations = details.sloaAuthorization;
      this.isEstateOrTestTrust = isEstateOrTestTrust;
      this.setTransactionTypes();
      this.resetForm(continueClicked);
      this.store.dispatch(resetCancelModal());
    });

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => {
      this.subAccountId = subAccountId;
      this.setPalAccountFlow();
    });

    this.standingAuthorizationsForm.controls.transactionType.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsFromEditInProgress),
          this.store.select(selectIsFromEditStarted)
        ),
        distinctUntilChanged()
      )
      .subscribe(([transactionType, isFromEditInProgress, isFromEditStarted]) => {
        this.transactionType = transactionType;
        this.store.dispatch(StandingAuthFormActions.initializeServiceError());
        if (!isFromEditInProgress && !isFromEditStarted) {
          if (this.standingAuthorizationsForm.controls.isSubmitAndCreateBundledSa) this.standingAuthorizationsForm.controls.isSubmitAndCreateBundledSa.setValue(false);
          this.store.dispatch(StandingAuthFormActions.transactionTypeChanged({ transactionType: transactionType }));
          if (transactionType === transactionTypes.internalTransfer) {
            this.serviceError = initialStandingAuthFormServiceError;
          }
          if (transactionType === transactionTypes.check) {
            this.store.dispatch(ModifiedFirstPartiesActions.getModifiedFirstParties({ subaccount: this.subAccountId, transactionType: 'checks' }));
            this.store.dispatch(
              StandingAuthCheckActions.callOneTimeInput({ isCheckSloa: true })
            );
          }
          if (transactionType === transactionTypes.wire) {
            this.store.dispatch(ModifiedFirstPartiesActions.getModifiedFirstParties({ subaccount: this.accountInfoState.accountId, transactionType: 'wires' }));
            this.store.dispatch(StandingAuthWireActions.callWireInputV3());
          }
        } else if (transactionType === transactionTypes.ach && this.loadTransactionsSuccess && !this.profilesUpdated) {
          this.callStandingAuthorizations();
        }
      });

    this.store
      .select(selectIsEmployee)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEmployee) => (this.isEmployee = isEmployee));

    this.store
      .select(selectHeaderStateServiceError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasError) => {
        this.hasHeaderStateError = hasError;
      });

    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(isPtrs => { this.isPtrs = isPtrs })

    this.store.select(selectRegType).pipe(takeUntil(this.destroy$)).subscribe(regType => { this.regType = regType })

    this.store.select(selectIsValidRegType).pipe(takeUntil(this.destroy$)).subscribe(isValidRegType => { this.isValidRegType = isValidRegType });

    this.store.select(selectStandingAuthForm).pipe(takeUntil(this.destroy$)).subscribe(form => {
      this.standingAuthForm = form;
    })

    this.store.select(selectInternalTransferInputCallFinished).pipe(takeUntil(this.destroy$)).subscribe(callFinished => {
      this.itInputCallFinished = callFinished;
    })

    this.store.select(selectAchInquiryCallFinished).pipe(takeUntil(this.destroy$)).subscribe(callFinished => {
      this.achInquiryCallSuccessful = callFinished;
    })

    this.store.select(selectCheckInquiryCallFinished).pipe(takeUntil(this.destroy$)).subscribe(callFinished => {
      this.checkInquiryCallSuccessful = callFinished;
    })

    this.store.select(selectCheckInputCallFinished).pipe(takeUntil(this.destroy$)).subscribe(callFinished => {
      this.checkInputCallFinished = callFinished;
    })


    this.store.select(selectWireInputV3CallFinished).pipe(takeUntil(this.destroy$)).subscribe(callFinished => {
      this.wireInputV3CallFinished = callFinished;
    })

    this.store.select(selectInternalTransferVerifyCallError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.hasATSSystemError = !!error;
      this.atsSystemErrorText = error;
    })

    this.store.select(selectCheckVerifyCallError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.hasATSSystemError = !!error;
      this.atsSystemErrorText = error;
    })

    this.tweUpcomingTransactions$ = this.store.select(selectTweUpcomingTransactions);
    this.tweStandingAuth$ = this.store.select(selectTweStandingAuth);
    this.tweRecurringTransactions$ = this.store.select(selectTweRecurringTransactions);
    this.taxWithholdings$ = this.store.select(selectStandingAuthACHTaxWithholdings);
    this.tweTableState$ = this.store.select(selectTweTableState);

    combineLatest([
      this.store.select(selectTwePopupInfo),
      this.store.select(selectSLOAVerifyCallFinished)
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([tweInfo, verifyCallFinished]) => {
      if (verifyCallFinished && tweInfo.openTwePopup) {
        this.showTweModal.emit(true);
      }
    })

    this.store.select(selectLoadTransactionsCallFinished).pipe(takeUntil(this.destroy$)).subscribe(loadTransactionsSuccess => {
      this.loadTransactionsSuccess = loadTransactionsSuccess;
      if (loadTransactionsSuccess && this.transactionType === transactionTypes.ach && !this.profilesUpdated) {
        this.callStandingAuthorizations();
      }
    })

    this.store.select(selectStandingAuthCallSucceededAch).pipe(takeUntil(this.destroy$)).subscribe(standingAuthCallFinished => {
      this.profilesUpdated = standingAuthCallFinished;
    })

    this.store.select(selectAchInquiryCallFinished).pipe(takeUntil(this.destroy$)).subscribe(inquiryCallSuccessful => {
      this.isEditReady = inquiryCallSuccessful;
    })

    this.store.select(selectShouldDisableFieldForEditQcd).pipe(takeUntil(this.destroy$)).subscribe(disableField => {
      this.shouldDisableForEditQcd = disableField;
    });

    this.store
      .select(selectStandingAuthFormPrefilledState)
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe((prefilledFlowState) => {
        switch (prefilledFlowState.state) {
          case 'started':
            this.initiateEditSubmittedProfile();
            break;
          case 'inProgress':
            switch (prefilledFlowState.name) {
              case 'edit-review':
                this.initiateEditFromReviewPage();
                break;
              default:
                break;
            }
            break;
          default:
            break;
        }
      });

    this.store
      .select(selectPageScrollTo)
      .pipe(
        takeUntil(this.destroy$),
        filter((val) => val === 'transaction-form-header')
      )
      .subscribe((_) => this.scrollToTop());

    this.store.select(selectIsBtMasterOnly).pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.isBtMasterOnly = val;
    });

    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.setPalAccountFlow();
        this.resetForm(true);
    });

    this.store.select(selectStandingAuthInternalTransferInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        if (info?.inputCallFinished) {
          const toAccountRegType = info?.toAccountInfo?.registrationType;
          this.skipJournalsVerify = toAccountRegType === RegTypeCode.INDIVIDUAL_401K || toAccountRegType === RegTypeCode.ROTH_401K;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setPalAccountFlow(): void {
    const contextType = this.coreService.currentContext?.contextType;
    this.isPalAccount = isPalAccountFlow(
      contextType,
      this.validateWireFlagsService.enablePalWireCreateTransaction()
    );
  }

  resetForm(reset: boolean): void {
    if (reset) {
      this.scrollToTop();
      this.standingAuthorizationsForm?.reset();
      if (this.standingAuthorizationsForm?.contains('extraPrompts')) {
        this.standingAuthorizationsForm?.removeControl('extraPrompts');
      }
      this.isEditSubmittedProfile = false;
      this.standingAuthFormHeader = 'Create standing authorization';
      this.standingAuthorizationsForm.controls.transactionType.enable();
      this.setTransactionTypes();
    }
  }

  initiateEditFromReviewPage(): void {
    this.standingAuthorizationsForm.controls.transactionType.setValue(this.stagedStandingAuthForm.transactionType);
    this.prefilledPromptsObs.next(this.stagedStandingAuthForm.standingAuthorizationDetails);
    this.isEditSubmittedProfile = false;
    this.standingAuthFormHeader = 'Create standing authorization';
    this.scrollToTop();
    this.setTransactionTypes();
    this.buildFormFromState();
  }

  initiateEditSubmittedProfile(): void {
    this.isEditSubmittedProfile = true;
    this.standingAuthFormHeader = 'Edit standing authorization';
    this.prefilledPromptsObs.next(this.stagedStandingAuthForm.standingAuthorizationDetails);
    this.scrollToTop();
    this.buildFormFromState();
    this.standingAuthorizationsForm.controls.transactionType.disable();
    if (this.transactionType !== 'check') {
      this.standingAuthorizationsForm.controls.extraPrompts?.disable();
    }
  }

  buildFormFromState(): void {
    this.standingAuthorizationsForm?.reset();
    if (this.stagedStandingAuthForm) {
      this.prefilledForm = this.stagedStandingAuthForm;
      this.standingAuthorizationsForm.controls.transactionType.setValue(
        this.stagedStandingAuthForm.transactionType
      );
      if (this.stagedStandingAuthForm.isSubmitAndCreateBundledSa) {
        createControl(this.standingAuthorizationsForm, 'isSubmitAndCreateBundledSa', this.stagedStandingAuthForm.isSubmitAndCreateBundledSa);
      }
      if (this.stagedStandingAuthForm.transactionType === 'ach')
      {
        let extraPrompts: AchExtraPrompts = {
          transferDirection: this.selectedRowDataInContext?.transferDirection ?? '',
          standingAuthId: this.selectedRowDataInContext?.standingAuthId,
          frequency: this.selectedRowDataInContext?.frequency
        };
        this.prefilledPromptsObsExtra.next(extraPrompts);
      } else if (this.stagedStandingAuthForm.transactionType === 'check') {
        let extraPrompts: CheckExtraPrompts = {}
        this.prefilledPromptsObsExtra.next(extraPrompts);
      }
    }
  }

  setTransactionTypes(): void {
    this.transactionTypeControl.radios = [];

    // Helper to check the index of a radio. Returns -1 if the radio doesn't exist.
    const findRadioIndex = (type: string): number => {
      return this.transactionTypeControl.radios.findIndex(radio => radio.value === type);
    }

    // Helper to add a new radio or replace a radio if it already exists for a particular transaction type
    const addOrReplaceRadio = (type: string, label: string[], index: number, disabled: boolean) => {
      const radio: any = { value: type, label: label };
      if (disabled) {
        radio.disabled = true;
        radio.warning = { text: this.transactionUnauthorizedText, displayBelow: false };
      }

      const existingIndex = findRadioIndex(type);
      if (existingIndex >= 0) {
        // Replace the existing radio
        this.transactionTypeControl.radios[existingIndex] = radio;
      } else {
        // Add a new radio
        this.transactionTypeControl.radios.splice(index, 0, radio);
      }
    };

    // --- ACH ---
    if (this.isEditSubmittedProfile) {
      addOrReplaceRadio(transactionTypes.ach, [transactionTypeLabels.ach], 0, false);
    }

    // --- Internal Transfer ---
    const enableInternalTransferRadio =
      this.sloaAuthorizations.includes(sloaAuthorization.journals) ||
      this.isPtrs ||
      this.isEmployee;
    addOrReplaceRadio(transactionTypes.internalTransfer, [transactionTypeLabels.internalTransfer], 1, !enableInternalTransferRadio);

    // --- Check ---
    const enableCheckRadio =
      this.sloaAuthorizations.includes(sloaAuthorization.check) ||
      this.isPtrs ||
      this.isEmployee;
    addOrReplaceRadio(transactionTypes.check, [transactionTypeLabels.check], 2, !enableCheckRadio);

    // --- Wire ---
    const enableWireRadio =
      this.sloaAuthorizations.includes(sloaAuthorization.wire) ||
      this.isPtrs ||
      this.isEmployee ||
      this.hasGoingDigitalFlowWireCreateSloa;
    addOrReplaceRadio(transactionTypes.wire, [transactionTypeLabels.wire], 3, !enableWireRadio);
  }

  onExtrasCompleted(completed: boolean): void {
    this.readyForTransactionDetails$.next(completed);
  }

  onExtraPromptsUpdated(): void {
    this.prefilledForm = undefined;
  }

  onContinueClick(): void {
    if (this.standingAuthorizationsForm.valid || this.standingAuthorizationsForm.disabled) {
      switch (this.standingAuthorizationsForm.controls.transactionType.value) {
        case transactionTypes.ach:
          if ((!this.serviceError.hasError || this.serviceError.name === ApiEndpointType.moneylinkVerify) && this.isEditReady) {
            this.store.dispatch(StandingAuthAchActions.callAchVerify(this.standingAuthForm));
          }
          break;
        case transactionTypes.internalTransfer:
          if ((!this.serviceError.hasError || this.serviceError.name === ApiEndpointType.internalTransferVerify) && this.itInputCallFinished) {
            // Going-digital flag removed; going digital is always enabled
            if (this.skipJournalsVerify) {
              this.store.dispatch(StandingAuthInternalTransferActions.loadInternalTransferReviewPageWithoutVerifyCall(this.standingAuthForm));
            } else {
              this.store.dispatch(StandingAuthInternalTransferActions.callInternalTransferVerify(this.standingAuthForm));
            }
          }
          break;
        case transactionTypes.wire:
          let isWireVerifyV3 = (!this.serviceError.hasError || this.serviceError.name === ApiEndpointType.wireVerifyV3) &&
            this.wireInputV3CallFinished;
          let DigitalToPdfFlow = (isWireVerifyV3) && ((this.useTestTrustDigitalToPdfFlow && !this.hasGoingDigitalFlowWireCreateSloa));

          if (DigitalToPdfFlow) {
            this.store.dispatch(StandingAuthWireActions.loadWiresReviewPageWithoutVerifyCall(this.standingAuthForm));
          } else {
            this.store.dispatch(StandingAuthWireActions.callWiresVerifyV3(this.standingAuthForm));
          }
          break;
        case transactionTypes.check:
          if ((!this.serviceError.hasError || this.serviceError.name === ApiEndpointType.checkVerify) && (this.checkInputCallFinished || this.checkInquiryCallSuccessful)) {
            this.store.dispatch(StandingAuthCheckActions.callVerify(this.standingAuthForm));
          }
          break;
      }
    } else {
      this.standingAuthorizationsForm.markAllAsTouched();
      updateGroupValueAndValidity(this.standingAuthorizationsForm);

      setTimeout(() => {
        const errorLabels = document.getElementsByClassName('error-text');
        if (errorLabels.length > 0) {
          const firstErrorEl = errorLabels[0] as HTMLElement;
          firstErrorEl?.focus();
          firstErrorEl?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
        }
      }, 0)
    }
  }

  onCancelClick(): void {
    if (this.standingAuthorizationsForm?.get('transactionType')?.value !== '') {
      this.store.dispatch(
        displayCancelModal({
          nextUrlState: navRouteConstants.standingAuth,
          title: 'Cancel this work in progress?',
          content: [
            'The information you have entered to create this standing authorization will be lost.',
          ],
          greenButton: 'Cancel standing authorization',
          grayButton: 'Back',
          source: 'standingAuthInputPage'
        })
      );
    }
    if (this.isFromEdit) {
      this.store.dispatch(StandingAuthReviewPageActions.editCompleted());
    }
  }

  callStandingAuthorizations() {
    let standingAuthRequest: StandingAuthorizationsRequest = {
      subAccountId: this.accountInfoState.accountId,
      masterAccountId: this.accountInfoState.masterAccount,
      sloaType: "A"
    }
    this.store.dispatch(StandingAuthorizationsActions.loadSloas(standingAuthRequest));
  }

  scrollToTop(): void {
    const elem = document.getElementById('standing-authorizations-form-header');
    setTimeout(() => {
      elem?.focus();
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    })
  }

  get transactionTypeControl(): RadioControl {
    return this.isEditSubmittedProfile
      ? this.initialTransactionTypeControl
      : {
        ...this.initialTransactionTypeControl,
        secondLineGreyText: {
          text: 'To create a new ACH (MoneyLink) standing authorization, go to ',
          linkText: 'Account Management.',
          linkRedirectLocation: 'actionCenter',
        },
      };
  }

  get displayCheckForm(): boolean {
    return this.checkInquiryCallSuccessful || this.checkInputCallFinished;
  }

  get displayAchForm(): boolean {
    return this.achInquiryCallSuccessful && this.profilesUpdated;
  }

  get showBundledToggle(): boolean {
    return this.hasBundledOffer && !!this.transactionType && this.transactionType !== 'ach' && !this.shouldDisableForEditQcd;
  }

  get showCreateRequestAccessDeniedAlert(): boolean {
    if (this.isEmployee) {
      return false;
    }
    return this.isValidRegType === false || this.hasMoveMoneyDataAccess === false;
  }

  getAccManagementRedirect(){
    const accManagementUrl = `${Constants.accountManagementRelative}`;
    window.open(accManagementUrl, '_blank')
  }

  get ineligibleRegTypeForStandingAuthorization(): boolean {
     // Going-digital flag removed; Guardianship and Conservatory reg types should show message directing them to StandingAuthorization
    return this.regType === shortRegTypes.pensionTrust
      || (this.regType === shortRegTypes.conservatorship || this.regType === shortRegTypes.guardianship);
  }

  get showDigitalToPdfIneligibleBanner(): boolean {
    return (this.transactionType !== undefined && this.isEstateOrTestTrust) &&
    (
      (this.transactionType === transactionTypes.wire && !this.hasGoingDigitalFlowWireCreateSloa) ||
      (this.transactionType === transactionTypes.internalTransfer && !this.hasGoingDigitalFlowInternalTransferCreateSloa)
    );
  }
}
